import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AppButton, Box, BoxExtendedProps, ChevronLeftIcon, ChevronRightIcon, Option } from '@common-fe/common-fe';

import HorizontalLine from '@/components/elements/HorizontalLine/HorizontalLine';
import { ClaimSummaryField } from '@/modules/UseMyMoney/UseMyMoney.types';

interface Props extends BoxExtendedProps {
  activeField?: ClaimSummaryField;
  setActiveField: (activeField?: ClaimSummaryField) => void;
  fields: Option[];
  isMobile?: boolean;
}

const FieldSetupFooter: React.FC<Props> = ({ activeField, setActiveField, fields, isMobile, ...props }) => {
  const { t } = useTranslation();

  const currentIndex = useMemo(
    () => fields?.findIndex((field) => field.key === activeField) ?? -1,
    [fields, activeField]
  );

  const nextFieldKey = useMemo(() => {
    if (currentIndex >= 0 && currentIndex < fields.length - 1) {
      return fields[currentIndex + 1].key;
    }
    return undefined;
  }, [fields, currentIndex]);

  const prevFieldKey = useMemo(() => {
    if (currentIndex > 0) {
      return fields[currentIndex - 1].key;
    }
    return undefined;
  }, [fields, currentIndex]);

  const handleNextField = useCallback(() => {
    if (nextFieldKey) {
      setActiveField(nextFieldKey as ClaimSummaryField);
    }
  }, [nextFieldKey, setActiveField]);

  const handlePrevField = useCallback(() => {
    if (prevFieldKey) {
      setActiveField(prevFieldKey as ClaimSummaryField);
    }
  }, [prevFieldKey, setActiveField]);

  const handleEnd = useCallback(() => {
    setActiveField(undefined);
  }, [setActiveField]);

  return (
    <Box data-testid="use-my-money-summary_field-setup-footer" {...props}>
      <HorizontalLine />

      <Box direction="row" gap="small">
        {prevFieldKey && isMobile && (
          <Box>
            <AppButton
              testId="go-to-prev-field"
              onClick={handlePrevField}
              buttonType="secondary"
              leftIcon={<ChevronLeftIcon />}
              width="100%"
            >
              {t('Back')}
            </AppButton>
          </Box>
        )} 

        {nextFieldKey ? (
          <Box flex={isMobile} width={isMobile ? undefined : '160px'}>
            <AppButton
              testId="go-to-next-field"
              onClick={handleNextField}
              buttonType={isMobile ? 'primary' : 'secondary'}
              rightIcon={<ChevronRightIcon />}
              width="100%"
            >
              {t('Next Step')}
            </AppButton>
          </Box>
        ) : (
          <Box flex={isMobile} width={isMobile ? undefined : '160px'}>
            <AppButton
              testId="go-to-claim-summary"
              onClick={handleEnd}
              buttonType={isMobile ? 'primary' : 'secondary'}
              width="100%"
            >
              {t("Claim's Summary")}
            </AppButton>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default FieldSetupFooter;
